import { useEffect, useState } from 'react';

import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  cn,
} from '@design-system';

interface PartnerShareConsentDialogProps {
  open: boolean;
  defaultChecked: boolean;
  isConnecting: boolean;
  onConfirm: (autoShare: boolean) => void;
  onCancel: () => void;
}

// Shown when the advisor confirms connecting partner contacts. Surfacing the auto-share opt-out as
// a dialog (pre-checked) gives the choice more weight than an inline checkbox. Confirming persists
// the choice and establishes the connection(s).
export function PartnerShareConsentDialog({
  open,
  defaultChecked,
  isConnecting,
  onConfirm,
  onCancel,
}: PartnerShareConsentDialogProps) {
  const [autoShare, setAutoShare] = useState(defaultChecked);

  useEffect(() => {
    if (open) setAutoShare(defaultChecked);
  }, [open, defaultChecked]);

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => !isOpen && !isConnecting && onCancel()}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-primary-dark">
            How Waterlily shares your client data
          </DialogTitle>
          <DialogDescription className="text-foreground">
            At intake, your clients consent to Waterlily sharing their data with you and the
            partners you collaborate with.
            <br />
            Based on that consent, Waterlily shares your client data with the team members you
            select, so you can work together without extra steps.
          </DialogDescription>
        </DialogHeader>

        <Label
          htmlFor="partner-auto-share"
          className={cn(
            'flex cursor-pointer items-start gap-3 rounded-md border p-4 transition-colors',
            { 'border-primary bg-primary/5': autoShare },
          )}
        >
          <Checkbox
            id="partner-auto-share"
            checked={autoShare}
            onCheckedChange={value => setAutoShare(value === true)}
            className="mt-0.5"
          />
          <span className="flex flex-col gap-1">
            <span className="text-foreground text-base font-medium">
              Waterlily automatically shares my clients with these contacts
            </span>
            <span className="text-muted-foreground text-sm">
              New clients you add are shared with them too. Uncheck to share clients yourself, one
              at a time, from your Clients table.
            </span>
          </span>
        </Label>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isConnecting}
          >
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(autoShare)}
            disabled={isConnecting}
          >
            {isConnecting ? 'Connecting…' : 'Acknowledge'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
